<!--
  A TRAILER, WITH NO SIGN THAT IT IS YOUTUBE.

  There is never a second one on the page — `trailer.svelte.ts` owns that rule
  and this component is only ever mounted by whoever holds the slot.

  It is a YouTube embed because there is nothing else: across 233 Cinemeta
  titles and 80 TMDB title pages, the number with a trailer we could stream into
  our own player is ZERO (docs/TRAILERS.md § A6). So the job is not to find a
  better supplier, it is to take YouTube's interface away — `yt.ts` explains
  that in full. What is left here is the picture.

  ── THE STILL IS THE PRODUCT, AND THE VIDEO IS THE BONUS ────────────────────

  Our own still is on top and opaque from the first frame. It leaves ONLY when
  the player has reported `PLAYING` and YouTube's own branding has had time to
  go away. Measured (docs/TRAILERS.md § B1): with `controls=0` the title bar,
  the channel avatar and the "YouTube" wordmark are on screen from the first
  frame of video until 3000–3400 ms later, every time, in three different
  videos. The old fixed 900 ms fade landed squarely inside that window — which
  is exactly what the owner saw — and revealing on `PLAYING` would have been the
  worst possible moment rather than the fix.

  So: hold the still for `STILL_HOLD_MS`, and at least `BRAND_MS` past PLAYING,
  then cross-fade. If the video never plays — an uploader who disabled
  embedding (~1 title in 10), an iPhone in Low Power Mode, a blocked script —
  nothing is removed from the screen and the still simply stays. That branch is
  not an error path; it is the common case on some devices and it has to look
  finished.

  ── THE BOX IS ALREADY 16:9, AND THE BARS ARE ZOOMED OUT OF IT ──────────────

  Every surface that mounts this hands it a 16:9 hole: the banner's stage, an
  expanded tile, the trailer sheet's screen. The old cover-crop maths is gone —
  16:9 is the only aspect YouTube delivers (docs/TRAILERS.md § E4) and cropping
  it into other shapes threw picture away.

  What DOES get cropped, carefully, is the letterbox burned into the video
  itself: a 2.39:1 trailer arrives with black bars inside the 16:9 raster, and
  they cut a hard edge against whatever surrounds the stage. `trailer-crop.ts`
  reads the true picture off the video's own mq1/mq2/mq3 frames (a bar is
  black in EVERY frame; a dark scene is not) and answers a zoom, capped at
  15% linear so uncertain detection or scope-format bars can never behead an
  actor — the iframe scales up inside this component's overflow-hidden box and
  the bars fall outside the clip. Uncertainty answers 1 and the bar honestly
  stays. The answer usually lands during the still hold; one that arrives
  after the reveal eases in over 600 ms rather than jumping.

  ── THE IFRAME IS BUILT IN JAVASCRIPT, ON PURPOSE ───────────────────────────

  `player.destroy()` removes the frame element itself. Leaving Svelte to own a
  node that a third-party library deletes is how teardown races start, so the
  frame is created into a container we hand it and torn down by the same effect
  that made it. It also means the `src` is written exactly once per trailer:
  changing a live embed's `src` is a second load, not a seek.
-->
<script lang="ts">
  import Icon from './Icon.svelte';
  import { trailerStill } from './cinemeta';
  import { cropFactorFor } from './trailer-crop';
  import { REVEAL_FADE_MS, STILL_HOLD_MS, revealIn, revealVerdict } from './trailer-rules';
  import { ENDED, attachYt, embedUrl, type YtHandle } from './yt';

  interface Props {
    /** YouTube id. */
    ytId: string;
    /**
     * Ids to fall back to when the uploader has disabled embedding. 35.6% of
     * titles carry a second one and ~1 in 10 needs it (docs/TRAILERS.md § A6).
     */
    alternates?: string[];
    /** OUR picture, held over the frame. Cinemeta `background`, never `ytimg`. */
    still?: string | undefined;
    /** Autoplay muted (a trailer that started itself) or with sound (a press). */
    muted?: boolean;
    /** Seconds in. This is what makes a press CONTINUE a trailer, not restart it. */
    start?: number;
    /** Fired once, when the picture reaches its end. */
    onended?: (() => void) | undefined;
    /** For the accessibility tree. */
    label?: string;
    /**
     * The minimum time the still holds. Five seconds where a trailer started by
     * itself — the owner asked for "five or six" — and zero for a deliberate
     * press, which still waits out the branding but not the courtesy.
     */
    holdMs?: number;
    /**
     * Offer a play button if the device refuses to autoplay. On where a tap has
     * nowhere better to go; off on a tile, where the card is already a link to
     * a page with a Trailer button on it.
     */
    tappable?: boolean;
    /**
     * DRAW NO STILL OF OUR OWN — the caller's surface IS the still phase.
     *
     * The banner cover-fills its whole frame with the slide's backdrop and
     * holds it unchanged until its own mirror of the reveal verdict says the
     * video is actually showing; a CONTAINED still inside this 16:9 box would
     * flash the full letterboxed thumbnail between those two pictures for the
     * whole pre-reveal beat (owner QA 2026-08-17: "I don't want to see that
     * transition of the thumbnail — I just want the video to start"). With
     * this set the still `<img>` is simply never rendered, so nothing of it
     * can bleed through a cross-fade either. THE CALLER TAKES ON THE STILL'S
     * OTHER TWO JOBS: covering the refusal branch (a device that never plays
     * keeps the caller's backdrop — the reveal verdict never fires there, so
     * the caller never uncovers this box) and covering the END SCREEN (the
     * banner unmounts the stage in the same tick as ENDED, advancing the
     * slideshow). A surface that keeps a finished Trailer mounted, or shows
     * this box before the reveal, must NOT set this.
     */
    noStill?: boolean;
    /** The live player, for a surface that draws its own controls. Null on teardown. */
    onplayer?: ((handle: YtHandle | null) => void) | undefined;
    /** Every state change, for the same reason. */
    onstate?: ((state: number) => void) | undefined;
    /**
     * True while decoded VIDEO is what is on screen — revealed and not ended,
     * the exact condition the cross-fade runs on. For a surface that overlays
     * a control which would be a lie over the still (a volume button, say).
     */
    onshowing?: ((showing: boolean) => void) | undefined;
  }

  let {
    ytId,
    alternates = [],
    still,
    muted = true,
    start = 0,
    label = 'Trailer',
    holdMs = STILL_HOLD_MS,
    tappable = false,
    noStill = false,
    onplayer,
    onstate,
    onshowing,
    onended
  }: Props = $props();

  /**
   * Which id is being tried. An `onError` moves it along the list; the still
   * never moves, so the only thing a visitor sees is that no video arrived.
   */
  let attempt = $state(0);
  const ids = $derived([ytId, ...alternates]);
  const activeId = $derived(ids[Math.min(attempt, ids.length - 1)] ?? ytId);
  $effect(() => {
    // A new subject starts the attempts again.
    void ytId;
    attempt = 0;
  });

  /* ── The still ──────────────────────────────────────────────────────────── */

  /**
   * `background` covers 120/120 titles, is genuinely 16:9 at every size, and in
   * the banner it has already been fetched for the slide — so it costs nothing.
   * `mqdefault` is the only ytimg size that is not a 4:3 pad of a 16:9 frame
   * (`hqdefault` is letterboxed in 98 of 98 cases) and it is the fallback, not
   * the choice: a YouTube thumbnail is often the uploader's own advertising.
   */
  let stillFailed = $state(false);
  const poster = $derived(
    !still || stillFailed ? trailerStill(activeId, 'md') : still
  );
  $effect(() => {
    void still;
    stillFailed = false;
  });

  /* ── The embed ──────────────────────────────────────────────────────────── */

  // The URL comes from yt.ts's `embedUrl` — ONE builder for every surface, so
  // this component and the persistent tile stage can never drift on the
  // parameters docs/TRAILERS.md § B3 measured one by one.

  let hole = $state<HTMLDivElement | null>(null);
  let player = $state<YtHandle | null>(null);

  /*
   * THE BAR CROP for this video — see the header. Never blocks anything: the
   * frame mounts at 1 and the zoom applies (through the `--crop` custom
   * property, so a late answer never touches the iframe node) whenever the
   * cached-or-measured verdict lands.
   */
  let crop = $state(1);
  $effect(() => {
    const id = activeId;
    let gone = false;
    crop = 1;
    void cropFactorFor(id).then((factor) => {
      if (!gone) crop = factor;
    });
    return () => {
      gone = true;
    };
  });

  /** When this attempt's frame was created — the clock the reveal runs on. */
  let openedAt = $state(0);
  let playingAt = $state<number | null>(null);
  let errored = $state(false);
  /** The video ran out, and YouTube has painted its end screen underneath. */
  let ended = $state(false);

  $effect(() => {
    const host = hole;
    const id = activeId;
    const withSound = !muted;
    const from = start;
    if (!host) return;

    const frame = document.createElement('iframe');
    frame.src = embedUrl(id, { muted: !withSound, start: from });
    frame.title = label;
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('loading', 'eager');
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    /*
     * WebKit raises a page error from inside the embed without `fullscreen`,
     * and WebKit's published policy gates cross-origin autoplay on `autoplay`.
     * Nothing here can act on either: the frame takes no clicks (below) and no
     * Tab, so it is never the thing being interacted with.
     */
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    /*
     * A TRAILER IS NOT A CONTROL. An iframe is focusable whatever its
     * pointer-events say, and a rail whose focused card swallows the next Tab
     * press is a keyboard trap; a click that reached YouTube's surface would
     * open youtube.com instead of the title page. Both are shut here.
     */
    frame.tabIndex = -1;
    host.replaceChildren(frame);

    openedAt = Date.now();
    playingAt = null;
    errored = false;
    ended = false;

    let gone = false;
    let handle: YtHandle | null = null;

    void attachYt(frame, {
      onplaying: () => {
        if (!gone && playingAt === null) playingAt = Date.now();
      },
      onerror: (code) => {
        if (gone) return;
        // 101 and 150 are "the uploader will not allow this embedded". Another
        // id might be, and a third of titles have one.
        if ((code === 101 || code === 150) && attempt + 1 < ids.length) attempt += 1;
        else errored = true;
      },
      onstate: (state) => {
        if (gone) return;
        /*
         * THE END SCREEN IS THE BRANDING COMING BACK.
         *
         * `rel=0` does not stop it and no parameter does: when the video runs
         * out, the player paints its own title, the channel name, a replay
         * button, a suggested-video thumbnail and the "YouTube" wordmark across
         * the picture. Measured on the title-page sheet after seeking to the
         * end — it is the exact thing the owner objected to, arriving at the
         * one moment nobody was watching for it.
         *
         * So the still comes back over it. Everything else about this component
         * is "do not uncover the frame until the branding has gone"; this is the
         * same rule at the other end of the video.
         */
        if (state === ENDED) {
          ended = true;
          onended?.();
        }
        onstate?.(state);
      }
    })
      .then((h) => {
        if (gone) {
          h.destroy();
          return;
        }
        handle = h;
        player = h;
        onplayer?.(h);
      })
      .catch(() => {
        // The API script was blocked. There is nothing to report and nothing to
        // fix: the still is already what is on screen, and `SILENCE_MS` turns
        // it into the final answer a few seconds from now.
      });

    return () => {
      gone = true;
      onplayer?.(null);
      player = null;
      handle?.destroy();
      host.replaceChildren();
    };
  });

  /* ── The reveal ─────────────────────────────────────────────────────────── */

  let revealed = $state(false);
  let silent = $state(false);

  $effect(() => {
    const input = { openedAt, playingAt, errored, holdMs };
    if (openedAt === 0) return;
    const settle = (): void => {
      const verdict = revealVerdict(input, Date.now());
      if (verdict === 'show') revealed = true;
      if (verdict === 'still-only') silent = true;
    };
    settle();
    const wait = revealIn(input, Date.now());
    if (!Number.isFinite(wait)) return;
    // One timer, armed exactly once, rather than a poll.
    const timer = setTimeout(settle, wait + 16);
    return () => clearTimeout(timer);
  });

  /*
   * Mirrors the class the cross-fade runs on, so a caller's overlay appears
   * with the video and leaves with it — including when the end screen brings
   * the still back. `false` on teardown so nobody is left holding a "showing"
   * for a frame that is gone.
   */
  $effect(() => {
    const showing = revealed && !ended;
    onshowing?.(showing);
    return () => onshowing?.(false);
  });

  /**
   * The one thing a tap can still rescue. Muted autoplay can be refused by Low
   * Power Mode and Low Data Mode, neither of which is detectable from
   * JavaScript — but a real gesture is a real gesture, so asking the player to
   * play from inside a click handler is the one move left. If it fails too,
   * nothing changes: the still was always what was on screen.
   */
  function askToPlay(): void {
    silent = false;
    ended = false;
    openedAt = Date.now();
    playingAt = null;
    player?.play();
  }
</script>

<div
  class="trailer"
  class:revealed={revealed && !ended}
  style="--fade: {REVEAL_FADE_MS}ms; --crop: {crop}"
>
  <div class="hole" bind:this={hole}></div>
  {#if !noStill}
    <img
      class="still"
      src={poster}
      alt=""
      decoding="async"
      onerror={() => (stillFailed = true)}
    />
  {/if}
  {#if tappable && (silent || ended)}
    <!--
      Not an error message. This device simply will not start a video by itself,
      which is Tuesday on an iPhone; the picture is finished as it is and this is
      an offer, not an apology.
    -->
    <button type="button" class="tap" onclick={askToPlay} aria-label="Play the {label}">
      <span class="disc"><Icon name="play" size={22} /></span>
    </button>
  {/if}
</div>

<style>
  .trailer {
    position: absolute;
    inset: 0;
    overflow: hidden;
    background: #000;
  }

  /*
   * THE FRAME IS UNDERNEATH AND THE STILL IS ON TOP, which is the whole design:
   * the picture is never taken away, it is only uncovered.
   */
  .hole {
    position: absolute;
    inset: 0;
  }
  .hole :global(iframe) {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
    pointer-events: none;
    /* The bar crop: scale up inside the clip so burned-in bars fall outside.
       1 for most videos; ≤1.15 ever (trailer-rules.ts CROP_MAX_SCALE). A
       transform, so YouTube still picks its quality rung from the LAYOUT
       size — the zoom costs zero bytes. */
    transform: scale(var(--crop, 1));
    transition: transform 600ms var(--ease);
  }

  .still {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 1;
    transition: opacity var(--fade) var(--ease);
  }
  .trailer.revealed .still {
    opacity: 0;
  }

  .tap {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    background: none;
  }
  .disc {
    display: grid;
    place-content: center;
    width: 62px;
    height: 62px;
    padding-left: 3px;
    border-radius: 50%;
    border: 1px solid var(--glass-line);
    background: rgb(8 10 15 / 0.55);
    backdrop-filter: var(--glass-blur);
    box-shadow: var(--glass-shine);
    color: var(--text);
    transition: transform var(--dur) var(--ease);
  }
  .tap:hover .disc {
    transform: scale(1.06);
  }
  .tap:focus-visible .disc {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    .still {
      transition: none;
    }
    .hole :global(iframe) {
      transition: none;
    }
    .tap:hover .disc {
      transform: none;
    }
  }
</style>
